import { App } from "antd";
import { Form, Formik, FormikValues } from "formik";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueries } from "@tanstack/react-query";
import * as Yup from "yup";

import { Button, Card, Input, Select } from "../../custom";
import styles from "./styles.module.scss";
import {
  getAllApplicationBatch,
  getAllModeOfStudy,
  getAllPrograms,
  registerUser,
} from "../../utils/auth_request";
import { validator } from "../../utils/validator";
import { routes } from "../../routes";
import {
  getAllProgramTypes,
  getCountries,
  getInstitutionByShortName,
  getLgaByStateId,
  getStateByCountryId,
} from "../../utils/request";
import { useState } from "react";
import { applicantTypeMap, INSTITUTION_NAME } from "../../utils/constants";
import useGetSpecializationByProgramIdQuery from "../../hooks/api/useGetSpecializationByProgramIdQuery";
import api from "../../utils/api";

const isBachelorProgramType = (programTypeName?: string) =>
  programTypeName?.trim().toLowerCase().startsWith("b") ?? false;

const knowAboutUsOptions = [
  { label: "LinkedIn", value: 0 },
  { label: "Instagram", value: 1 },
  { label: "Facebook", value: 2 },
  { label: "Website", value: 3 },
  { label: "Referrals", value: 4 },
  { label: "Current Student", value: 5 },
  { label: "Faculty Member", value: 6 },
  { label: "Alumni Network", value: 7 },
  { label: "Newspaper Publication", value: 8 },
].map((item) => (
  <option key={item.value} value={item.value}>
    {item.label}
  </option>
));

const Register = () => {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const { programTypeId, departmentId, id } = useParams();
  const [countryId, setCountryId] = useState<number>(0);
  const [stateId, setStateId] = useState<number>(0);
  const programId = Number(departmentId);
  const mainProgramId = Number(id);

  const specializationQuery = useGetSpecializationByProgramIdQuery({ Id: mainProgramId });

  const registerMutation = useMutation({
    mutationKey: ["register"],
    mutationFn: registerUser,
  });

  const queries = useQueries({
    queries: [
      {
        queryKey: ["get-institution"],
        queryFn: () => getInstitutionByShortName(INSTITUTION_NAME || ""),
        retry: 1,
      },
      {
        queryKey: ["get-application-batch"],
        queryFn: getAllApplicationBatch,
        retry: 1,
      },

      {
        queryKey: ["get-mode-of-study"],
        queryFn: getAllModeOfStudy,
        retry: 1,
      },
      {
        queryKey: ["get-departments"],
        queryFn: getAllPrograms,
        retry: 1,
      },
      {
        queryKey: ["get-programsTypes"],
        queryFn: getAllProgramTypes,
        retry: 1,
      },
      {
        queryKey: ["get-countries"],
        queryFn: getCountries,
      },
      {
        queryKey: ["get-state-id", countryId],
        queryFn: () => getStateByCountryId(countryId),
        enabled: !!countryId,
        refetchOnWindowFocus: true,
      },
      {
        queryKey: ["get-lga-id", stateId],
        queryFn: () => getLgaByStateId(stateId),
        enabled: !!stateId,
        refetchOnWindowFocus: true,
      },
      {
        queryKey: ["get-program-details"],
        queryFn: async () =>
          (await api.get(`/Utilities/Utilities/GetAllProgramDetails`))
            ?.data as ProgramDataResponse,
        retry: 1,
      },
    ],
  });

  const institutionQuery = queries[0];
  const applicationBatchDataQuery = queries[1];
  const modeOfStudyDataQuery = queries[2];
  const programDataQuery = queries[3];
  const programTypeQuery = queries[4];
  const countryQuery = queries[5];
  const stateQuery = queries[6];
  const lgaQuery = queries[7];
  const programDetailsQuery = queries[8];

  const institutionData = institutionQuery?.data?.data ?? ({} as School);
  const applicationBatchData = applicationBatchDataQuery?.data?.data ?? [];
  const modeOfStudyData = modeOfStudyDataQuery?.data?.data ?? [];
  const programData = programDataQuery?.data?.data ?? [];
  const programTypeData = programTypeQuery?.data?.data ?? [];
  const countryData = countryQuery?.data?.data ?? [];
  const stateData = stateQuery?.data?.data ?? [];
  const lgaData = lgaQuery?.data?.data ?? [];
  const programDetailsData = programDetailsQuery?.data?.data ?? [];

  const activeModeOfStudy = modeOfStudyData?.filter(
    (mode) => mode.activeStatus === true,
  );
  
  const findProgramType = programTypeData?.find(
    (item) => item?.id === Number(programTypeId),
  );
  const isBachelorProgram = isBachelorProgramType(findProgramType?.name);
  
  const findDepartment = programData?.find(
    (item) => item?.id === Number(departmentId),
  );
  const selectedProgram = programDetailsData?.find(
    (item) =>
      item?.programId === Number(departmentId) 
    && item?.programTypeId == Number(programTypeId),
  );  

  const selectedProgramName =
    selectedProgram?.programName ?? findDepartment?.name ?? "";

  const applicationBatchOptions = applicationBatchData?.map((item) => (
    <option key={item?.id} value={item.id}>
      {item.batchName}
    </option>
  ));

  const modeOfEntryData = [
    { label: "UTME", value: 1 },
    { label: "Direct Entry", value: 2 },
    // { label: "Transfer", value: 3 },
  ];

  const modeOfEntryOptions = modeOfEntryData?.map((item) => (
    <option key={item?.value} value={item?.value}>
      {item?.label}
    </option>
  ));

  const modeOfStudyOptions = activeModeOfStudy?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.name}
    </option>
  ));

  const countryOptions = countryData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.countryName}
    </option>
  ));

  const stateOptions = stateData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.stateName}
    </option>
  ));

  const lgaOptions = lgaData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.lgaName}
    </option>
  ));

  const specializationOptions = specializationQuery.specializations.map((item) => (
    <option key={item.id} value={item.id}>
      {item.name}
    </option>
  ));

  const validationSchema = Yup.object().shape({
    email: validator.email,
    firstName: validator.firstName,
    lastName: validator.lastName,
    phone: validator.phone,
    password: validator.password,
    confirmPassword: validator.confirmPassword,
    department: validator.department,
    selectModeOfStudy: validator.selectModeOfStudy,
    selectApplication: validator.selectApplication,
    programType: validator.selectProgramType,
    knowAboutUs: validator.knowAboutUs,
  });

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const getApplicantType = (countryId: number, stateId: number) => {
    if (countryId === institutionData?.countryId) {
      if (stateId === institutionData?.stateId) {
        return applicantTypeMap.Indigene;
      } else return applicantTypeMap.Regular;
    } else return applicantTypeMap.International;
  };

  const handleRegister = async (values: FormikValues) => {
    const applicantType = getApplicantType(countryId, stateId);
    const payload: RegisterPayload = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.phone,
      password: values.password,
      confirmPassword: values.confirmPassword,
      programId,
      modeOfStudyId: Number(values.selectModeOfStudy),
      applicationBatchId: Number(values.selectApplication),
      programTypeId: Number(programTypeId),
      concentrationId: Number(values.selectSpecialization) || 0,
      applicantType: applicantType,
      countryId: countryId,
      stateId: stateId,
      lgaId: Number(values.lga),
      knowAboutUs: Number(values.knowAboutUs),
    };

    if (isBachelorProgram) {
      payload.modeOfEntry = Number(values.selectModeOfEntry);
    }

    try {
      await registerMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });

          navigate(`${routes.auth.verifyEmail}?email=${values.email}`);
          // resetForm();
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  return (
    <section className={styles.container}>
      <div>
        <Card>
          <div className={styles.cardChild}>
            <div>
              <h3>Welcome to our portal</h3>
              <p>To Register please enter your information below</p>
            </div>

            <div>
              <div>
                <Formik
                  initialValues={{
                    email: "",
                    firstName: "",
                    phone: "",
                    lastName: "",
                    password: "",
                    confirmPassword: "",
                    selectApplication: "",
                    selectModeOfStudy: "",
                    selectSpecialization: "",
                    selectedProgram: selectedProgramName,
                    department: selectedProgram?.program ?? "",
                    programType: selectedProgram?.programTypeName ?? "",
                    country: "",
                    state: "",
                    lga: "",
                    knowAboutUs: "",
                  }}
                  onSubmit={(values, resetForm) => {
                    handleRegister(values);
                  }}
                  enableReinitialize={true}
                  validationSchema={validationSchema}
                >
                  {({ handleChange, setFieldValue }) => {
                    return (
                      <Form>
                        <div className={styles.formStyle}>
                          <Select
                            name="selectApplication"
                            placeholder="Select Application Batch"
                            label="Select Application Batch:"
                            options={applicationBatchOptions}
                            onChange={handleChange}
                          />

                          <Input name="firstName" label="First Name:" />
                          <Input name="lastName" label="Last Name:" />
                          <Input name="email" label="Email:" />
                          <Input name="phone" label="Phone Number:" />
                          <Select
                            label="Select Country:"
                            name="country"
                            placeholder="Select Country"
                            options={countryOptions}
                            onChange={(e) => {
                              handleChange(e);
                              setCountryId(parseInt(e.target.value));
                              setFieldValue("country", e.target.value);
                            }}
                          />
                          <Select
                            label="Select State:"
                            name="state"
                            placeholder="Select State"
                            options={stateOptions}
                            onChange={(e) => {
                              handleChange(e);
                              setStateId(parseInt(e.target.value));
                              setFieldValue("state", e.target.value);
                            }}
                            disabled={!countryId}
                          />
                          <Select
                            label="Select LGA:"
                            name="lga"
                            placeholder="Select Local Government Area"
                            options={lgaOptions}
                            onChange={handleChange}
                            disabled={!stateId}
                          />
                          <Input
                            type="password"
                            name="password"
                            label="Password:"
                          />
                          <Input
                            name="confirmPassword"
                            label="Confirm Password:"
                            type="password"
                          />

                          <Select
                            label="Select Mode of Study:"
                            name="selectModeOfStudy"
                            placeholder="Select Mode Of Study"
                            options={modeOfStudyOptions}
                            onChange={handleChange}
                          />
                          {isBachelorProgram && (
                            <Select
                              label="Select Mode of Entry:"
                              name="selectModeOfEntry"
                              placeholder="Select Mode Of Entry"
                              options={modeOfEntryOptions}
                              onChange={handleChange}
                            />
                          )}
                            <Input
                            name="department"
                            label="Department"
                            readOnly
                            disabled
                          />
                            <Input
                            name="programType"
                            label="Program Type"
                            readOnly
                            disabled
                          />
                         
                          {specializationQuery.hasSpecializations && (
                            <Select
                              label="Select Specialization/Concentration:"
                              name="selectSpecialization"
                              placeholder="Select Specialization"
                              options={specializationOptions}
                              onChange={handleChange}
                            />
                          )}
                          <Select
                            label="How did you hear about us:"
                            name="knowAboutUs"
                            placeholder="Select Option"
                            options={knowAboutUsOptions}
                            onChange={handleChange}
                          />
                        </div>

                        <div>
                          <div className={styles.buttonGroup}>
                            <Button
                              type="submit"
                              text="REGISTER"
                              disabled={registerMutation.isPending}
                              isLoading={registerMutation?.isPending}
                            />

                            <Button
                              type="button"
                              text="BACK"
                              onClick={handleBackNavigation}
                            />
                          </div>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </div>
            </div>
          </div>

          <div className={styles.footer}>
            <Link to={routes.auth.login}>
              <p>Already have an account? Login</p>
            </Link>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default Register;
